import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import moment from "moment";
import ReactDataTable from "../ReactDataTable";
import BaseImage from "../BaseImage";
import ReviewSwitch from "../ReviewSwitch";
import LockStatusSwitch from "../LockStatusSwitch";
import { DEMO_REPORTS } from "../SectionHealthReport";
import usePermissions from "../../hooks/use-permissions";
import { ViewPermissions } from "../../constants";
import { useMutation } from "@apollo/client";
import { ReportUnLock } from "../../apollo-client/api-query";
import { hasViewReportPermission } from "../../utils/permission";

const PatientReportsTable = ({ reports = [], loadingGetReports, refetchReports, }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const containerRef = useRef(null);
  const {myPermissions} = usePermissions();
  const [unlockReport, { loading: loadingUnlockReport }] = useMutation(ReportUnLock, {fetchPolicy: 'network-only'})

  const [selectedRows, setSelectedRows] = useState([])

  const getViewReportContent = useCallback((row) => {
    // can view incompleteReports?
    if (myPermissions?.includes(ViewPermissions.VIEW_INCOMPLETED_REPORTS)) {
      if (row?.needsReview) return 'Needs review'
      else return 'View report'
    } else {
      if (row?.needsReview) return 'Pending'
      else return 'View full report'
    }
  }, [myPermissions])

  const columns = useMemo(() => ([
    {
      name: 'REPORT NAME',
      selector: row => row.name,
      sortable: true,
      minWidth: '270px',
      cell: (row) => {
        return (
          <Box
            sx={{
              minWidth: '270px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <BaseImage
              sx={{
                minWidth: '30px',
                width: '30px',
                height: '30px',
              }}
              src={DEMO_REPORTS.find(item => item.name === row?.name)?.icon}
              alt={row?.name}
            />
            <Box
              sx={{
                ml: '16px'
              }}
            >
              <Typography variant="h3">
                {row?.name}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      name: 'DATE',
      selector: row => row.dataUpdatedAt,
      sortable: true,
      cell: (row) => {
        return (
          <Box
            sx={{
              display: !row?.needsReview ? 'block' : 'none',
            }}
          >
            <Typography variant="body" color="primary.darkGray">
              {moment(row?.updatedAtOrg).format('MM/DD/YYYY')}
            </Typography>
          </Box>
        )
      },
    },
    {
      name: 'RESULTS',
      selector: row => row.results,
      sortable: false,
      cell: (row) => {
        return (
          <Box
            sx={{
              display: !row?.needsReview ? 'block' : 'none',
              textAlign: 'center',
              bgcolor: row?.priority === 0 ? 'primary.lightGreen1' : 'primary.lightRed',
              padding: '1px 7px',
              borderRadius: '4px'
            }}
          >
            <Typography
              variant="subText"
              color={row.priority === 0 ? "primary.darkGreen" : "primary.red"}
            >
              {`${row?.priority || 0} Priority`}
            </Typography>
          </Box>
        )
      },
    },
    {
      name: 'REVIEW STATUS',
      selector: row => row?.needsReview,
      sortable: true,
      minWidth: '130px',
      cell: (row) => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <ReviewSwitch
              row={row}
              reportId={row?.id}
              needsReview={row?.needsReview}
              refetchReports={refetchReports}
              isReadonly = {!myPermissions.includes(ViewPermissions.UPDATE_REVIEW)}
            />
          </Box>
        )
      },
    },
    {
      name: 'LOCK STATUS',
      selector: row => row.locked,
      sortable: false,
      minWidth: '130px',
      cell: (row) => {
        return (
          <Box
            sx={{
              alignItems: 'center',
              width: '100%'
            }}
          >
            <LockStatusSwitch
              row={row}
              reportId={row?.id}
              lockStatus={row?.locked}
              refetchReports={refetchReports}
              isReadonly = {!myPermissions.includes(ViewPermissions.UPDATE_LOCK)}
            />
          </Box>
        )
      },
    },
    {
      id: 'view-full-reports',
      name: 'ACTION',
      sortable: false,
      ignoreRowClick: true,
		  allowoverflow: true,
      cell: (row) => {
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {getViewReportContent(row) !== 'Pending' ? (
              <Box 
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: getViewReportContent(row) !== 'View full report' ? (myPermissions?.includes(ViewPermissions.VIEW_INCOMPLETED_REPORTS) && row?.needsReview) ? "primary.mainBlue" : "primary.darkBlue" : 'primary.mainBlue'
                }}
                onClick={() => {
                  if (row?.name === 'Medications') {
                    navigate(`/patient/${id}/medication-reports`)
                  } else if (row?.name === 'Nutrition') {
                    navigate(`/patient/${id}/nutrition-report`)
                  } else {
                    navigate(`/patient/${id}/reports/${row?.value}`)
                  }
                }}
              >
                {getViewReportContent(row) !== 'View full report' ? (
                  <>
                    <Typography
                      variant="body"
                      color={(myPermissions?.includes(ViewPermissions.VIEW_INCOMPLETED_REPORTS) && row?.needsReview) ? "primary.mainBlue" : "primary.darkBlue"}>
                      {getViewReportContent(row)}
                    </Typography>
                    <NavigateNextIcon sx={{
                      color: (myPermissions?.includes(ViewPermissions.VIEW_INCOMPLETED_REPORTS) && row?.needsReview) ? "primary.mainBlue" : "primary.darkBlue"
                    }} />
                  </>
                ) : (
                  <Typography
                    variant="body"
                    color="primary.mainBlue"
                  >
                    {getViewReportContent(row)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  bgcolor: 'primary.mediumGray',
                  borderRadius: '40px',
                  height: '31px',
                  minWidth: '87px',
                  padding: '4px 12px'
                }}
              >
                <Typography variant="h4" color="white">
                  Pending
                </Typography>
              </Box>
            )}
          </Box>
        )
      },
    },
  ]), [id, myPermissions, navigate, refetchReports, getViewReportContent])

  const columnDefs = useMemo(() => {
    const visibleFields = [...columns]
    const filteredColumns = visibleFields
    .filter(item => myPermissions.includes(ViewPermissions.VIEW_REPORT) || 
                    item.name !== 'LOCK STATUS')
    .filter(item => myPermissions.includes(ViewPermissions.VIEW_REPORT) || item.name !== 'REVIEW STATUS')
    .filter(item => hasViewReportPermission(myPermissions) || item.name !== 'ACTION')
    return filteredColumns
  }, [myPermissions, columns])

  // The only bulk action is "Unlock selected", so gate both the row checkboxes
  // AND the Unlock button on UPDATE_LOCK. Roles without it (e.g. Sales) see
  // neither the select column nor the button — there'd be nothing to do with
  // a selection anyway.
  const canBulkUnlock = myPermissions.includes(ViewPermissions.UPDATE_LOCK);

  const handleChangeSelectRows = (state) => {
    setSelectedRows(state.selectedRows);
  };

  const handleClickUnlockSelected = () => {
    const reportIdArray = selectedRows.filter(item => item.locked).map(item => item.id)

    reportIdArray.forEach(async id => {
      const variables = {
        variables: {
          reportId: id
        }
      }
      await unlockReport(variables)
      refetchReports()
    })

    if (!loadingUnlockReport && !loadingGetReports) {
      setSelectedRows(selectedRows.map(item => ({ ...item, locked: false })))
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {canBulkUnlock && (
        <Box>
          <Typography
            variant="body"
            color="primary.darkGray"
            sx={{
              borderBottom: '1px solid',
              borderColor: 'primary.mediumGray',
              cursor: 'pointer'
            }}
            onClick={handleClickUnlockSelected}
          >
            Unlock selected
          </Typography>
        </Box>
      )}
      <Box ref={containerRef} sx={{ mt: '8px' }}>
        <ReactDataTable
          columnDefs={columnDefs}
          rowData={reports}
          page='patient-detail'
          selectableRows={canBulkUnlock}
          // selectableRowSelected={handleRowSelect}
          selectedRows={selectedRows}
          onSelectedRowsChange={handleChangeSelectRows}
          loadingData={loadingGetReports || loadingUnlockReport}
          noText='Reports are not ready for review'
        />
      </Box>
    </Box>
  )
}

export default PatientReportsTable